import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

// Re-declared inline so this component is self-contained — same tokens as Reports.jsx.
const btnPrimary = { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' };
const btnDanger = { background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const cardStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContent = { background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', width: '640px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '1px solid var(--border-color)' };

const authHeaders = () => {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const RLS_OPS = [
  { key: 'eq', label: '=' },
  { key: 'ne', label: '≠' },
  { key: 'in', label: 'in' },
  { key: 'not_in', label: 'not in' },
  { key: 'contains', label: 'contains' },
  { key: 'is_null', label: 'is null' },
  { key: 'is_not_null', label: 'is not null' },
];

// ──────────────────────────────────────────────────────────────────────────
// Datasets
// ──────────────────────────────────────────────────────────────────────────

export function DatasetsView({ workspaceId }) {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | 'new' | dataset row
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/custom-datasets`, { headers: authHeaders() });
      setDatasets(r.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dataset? Reports referencing it will be blocked first.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/custom-datasets/${id}`, { headers: authHeaders() });
      await load();
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
          {datasets.length} custom dataset{datasets.length !== 1 ? 's' : ''} · plus 12 built-ins. Custom SQL + RLS require Pro/Enterprise.
        </div>
        <button onClick={() => setEditing('new')} style={btnPrimary}>+ New dataset</button>
      </div>
      {error && <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: '6px', marginBottom: '12px' }}>{error}</div>}
      {datasets.length === 0 ? (
        <div style={{ ...cardStyle, maxWidth: '520px', margin: '40px auto', textAlign: 'center', padding: '32px' }}>
          <div style={{ fontSize: '36px' }}>🗄️</div>
          <div style={{ fontSize: '15px', fontWeight: 600, marginTop: '8px' }}>No custom datasets</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Paste a SELECT statement to expose new columns to the report designer.</div>
          <div style={{ marginTop: '14px' }}><button onClick={() => setEditing('new')} style={btnPrimary}>+ New dataset</button></div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {datasets.map(d => (
            <div key={d.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>slug: <code>{d.slug}</code></div>
                </div>
                <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent-color)', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px' }}>{d.source_type}</span>
              </div>
              {d.description && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '6px' }}>{d.description}</div>}
              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '8px' }}>
                Columns: {d.schema?.length || 0} · RLS: {d.rls_rule_set?.length ? `${d.rls_rule_set.length} rule(s)` : 'off'} · Masks: {d.column_masks?.length ? `${d.column_masks.length}` : 'none'}
              </div>
              {!d.is_active && <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '4px' }}>Deactivated</div>}
              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                <button onClick={() => setEditing(d)} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 8px' }}>Edit</button>
                <button onClick={() => handleDelete(d.id)} style={{ ...btnDanger, fontSize: '11px', padding: '4px 8px' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <DatasetEditor
          workspaceId={workspaceId}
          dataset={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function DatasetEditor({ workspaceId, dataset, onClose, onSaved }) {
  const isNew = !dataset;
  const [slug, setSlug] = useState(dataset?.slug || '');
  const [name, setName] = useState(dataset?.name || '');
  const [description, setDescription] = useState(dataset?.description || '');
  const [sourceType, setSourceType] = useState(dataset?.source_type || 'sql');
  const [credentialId, setCredentialId] = useState(dataset?.credential_id || '');
  const [sqlText, setSqlText] = useState(dataset?.sql_text || '-- SELECT id, status, created_at FROM cases WHERE workspace_id = :workspace_id LIMIT 1000');
  const [columns, setColumns] = useState(dataset?.schema || []);
  const [rlsRules, setRlsRules] = useState(dataset?.rls_rule_set || []);
  const [columnMasks, setColumnMasks] = useState(dataset?.column_masks || []);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/datasets/validate-sql`,
        { sql_text: sqlText, credential_id: credentialId ? Number(credentialId) : null, sample_rows: 5 },
        { headers: authHeaders() },
      );
      setValidation(r.data);
      setColumns(r.data.columns || columns);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        description: description || null,
        sql_text: sqlText,
        credential_id: credentialId ? Number(credentialId) : null,
        schema_json: columns,
        rls_rule_set: rlsRules,
        column_masks: columnMasks,
      };
      if (isNew) {
        await axios.post(
          `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/datasets`,
          { ...payload, slug, source_type: sourceType },
          { headers: authHeaders() },
        );
      } else {
        await axios.patch(
          `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/custom-datasets/${dataset.id}`,
          payload,
          { headers: authHeaders() },
        );
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const addRlsRule = () => setRlsRules([...rlsRules, { column: columns[0]?.key || '', op: 'eq', value: '{var:current_user.id}' }]);
  const updateRule = (i, patch) => setRlsRules(rlsRules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setRlsRules(rlsRules.filter((_, idx) => idx !== i));

  const addMask = () => setColumnMasks([...columnMasks, { column: columns[0]?.key || '', redact_for_roles: ['viewer'], replacement: '***' }]);
  const updateMask = (i, patch) => setColumnMasks(columnMasks.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const removeMask = (i) => setColumnMasks(columnMasks.filter((_, idx) => idx !== i));

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: '16px' }}>{isNew ? 'New custom dataset' : `Edit: ${dataset.name}`}</h3>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
          Reference <code>:workspace_id</code> in your SQL — the engine binds it automatically and rejects DDL/DML.
        </div>

        {isNew && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
            <div>
              <label style={labelStyle}>Slug</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="open_cases_by_team" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Source type</label>
              <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} style={inputStyle}>
                <option value="sql">Operational DB (SQL)</option>
                <option value="credential">External (via credential)</option>
              </select>
            </div>
          </div>
        )}

        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Open Cases By Team" style={inputStyle} />
        </div>
        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
        </div>
        {sourceType === 'credential' && (
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>Credential ID</label>
            <input value={credentialId} onChange={(e) => setCredentialId(e.target.value)} placeholder="42" style={inputStyle} />
          </div>
        )}
        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>SQL (SELECT only)</label>
          <textarea
            value={sqlText}
            onChange={(e) => setSqlText(e.target.value)}
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px', minHeight: '140px', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button onClick={handleValidate} disabled={validating} style={btnSecondary}>{validating ? 'Validating…' : 'Validate / sample'}</button>
            {validation && <span style={{ fontSize: '11px', color: '#10b981', alignSelf: 'center' }}>✓ {validation.columns?.length} columns · {validation.sample?.length} sample rows</span>}
          </div>
        </div>

        {columns.length > 0 && (
          <div style={{ marginTop: '14px' }}>
            <label style={labelStyle}>Columns ({columns.length})</label>
            <div style={{ ...cardStyle, padding: '8px', maxHeight: '180px', overflow: 'auto' }}>
              {columns.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '6px', marginBottom: '4px', fontSize: '12px' }}>
                  <input value={c.key || ''} onChange={(e) => setColumns(columns.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} style={{ ...inputStyle, fontSize: '12px' }} />
                  <input value={c.label || ''} onChange={(e) => setColumns(columns.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} style={{ ...inputStyle, fontSize: '12px' }} />
                  <select value={c.type || 'text'} onChange={(e) => setColumns(columns.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x))} style={{ ...inputStyle, fontSize: '12px' }}>
                    {['text', 'number', 'date', 'datetime', 'bool'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={labelStyle}>Row-level security ({rlsRules.length})</label>
            <button onClick={addRlsRule} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ rule</button>
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
            Use <code>{'{var:current_user.id}'}</code> for the requesting user; rules AND together; missing context fails closed.
          </div>
          {rlsRules.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr 30px', gap: '4px', marginBottom: '4px' }}>
              <input value={r.column || ''} onChange={(e) => updateRule(i, { column: e.target.value })} placeholder="column" style={{ ...inputStyle, fontSize: '12px' }} />
              <select value={r.op || 'eq'} onChange={(e) => updateRule(i, { op: e.target.value })} style={{ ...inputStyle, fontSize: '12px' }}>
                {RLS_OPS.map(op => <option key={op.key} value={op.key}>{op.label}</option>)}
              </select>
              <input
                value={r.value === undefined || r.value === null ? '' : (Array.isArray(r.value) ? r.value.join(',') : String(r.value))}
                onChange={(e) => updateRule(i, { value: e.target.value })}
                placeholder="value or {var:current_user.id}"
                style={{ ...inputStyle, fontSize: '12px' }}
              />
              <button onClick={() => removeRule(i)} style={{ ...btnDanger, padding: '3px 6px', fontSize: '11px' }}>×</button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={labelStyle}>Column masks ({columnMasks.length})</label>
            <button onClick={addMask} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ mask</button>
          </div>
          {columnMasks.map((m, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 30px', gap: '4px', marginBottom: '4px' }}>
              <input value={m.column || ''} onChange={(e) => updateMask(i, { column: e.target.value })} placeholder="column" style={{ ...inputStyle, fontSize: '12px' }} />
              <input value={(m.redact_for_roles || []).join(',')} onChange={(e) => updateMask(i, { redact_for_roles: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="viewer,editor" style={{ ...inputStyle, fontSize: '12px' }} />
              <input value={m.replacement || ''} onChange={(e) => updateMask(i, { replacement: e.target.value })} placeholder="***" style={{ ...inputStyle, fontSize: '12px' }} />
              <button onClick={() => removeMask(i)} style={{ ...btnDanger, padding: '3px 6px', fontSize: '11px' }}>×</button>
            </div>
          ))}
        </div>

        {error && <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: '6px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !name || (isNew && !slug)} style={btnPrimary}>{saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Embed tokens
// ──────────────────────────────────────────────────────────────────────────

export function EmbedTokensView({ workspaceId, reports }) {
  const [selectedReportId, setSelectedReportId] = useState('');
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [error, setError] = useState(null);

  const loadTokens = useCallback(async (rid) => {
    if (!rid) { setTokens([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${rid}/embed-tokens`, { headers: authHeaders() });
      setTokens(r.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { loadTokens(selectedReportId); }, [selectedReportId, loadTokens]);

  const handleMint = async (form) => {
    setError(null);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${selectedReportId}/embed-token`,
        form,
        { headers: authHeaders() },
      );
      setNewToken(r.data);
      setCreating(false);
      await loadTokens(selectedReportId);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this token? Any iframe using it will stop working immediately.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/embed-tokens/${id}`, { headers: authHeaders() });
      await loadTokens(selectedReportId);
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '14px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Report</label>
          <select value={selectedReportId} onChange={(e) => setSelectedReportId(e.target.value)} style={inputStyle}>
            <option value="">— Pick a report —</option>
            {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <button onClick={() => setCreating(true)} disabled={!selectedReportId} style={btnPrimary}>+ Mint token</button>
      </div>

      {error && <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: '6px', marginBottom: '12px' }}>{error}</div>}

      {newToken && (
        <div style={{ ...cardStyle, marginBottom: '14px', borderColor: 'var(--accent-color)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600 }}>New embed token (copy now — JWT only shown once)</div>
          <textarea readOnly value={newToken.jwt || ''} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '11px', minHeight: '70px', marginTop: '6px' }} />
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '6px' }}>
            Embed URL: <code>/api/reports/embed/{newToken.report_id}?token=…</code> · expires {newToken.expires_at}
          </div>
          <button onClick={() => setNewToken(null)} style={{ ...btnSecondary, marginTop: '8px', fontSize: '11px' }}>Done</button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
      ) : !selectedReportId ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-tertiary)', padding: '32px' }}>Pick a report to manage its embed tokens.</div>
      ) : tokens.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-tertiary)', padding: '32px' }}>No tokens minted yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>Label</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Expires</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>RLS user</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Used</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(t => (
              <tr key={t.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '8px' }}>{t.label || '—'}</td>
                <td style={{ padding: '8px' }}>{t.expires_at ? new Date(t.expires_at).toLocaleString() : '—'}</td>
                <td style={{ padding: '8px' }}>{t.rls_user_id || '—'}</td>
                <td style={{ padding: '8px' }}>
                  {t.revoked_at ? <span style={{ color: '#ef4444' }}>revoked</span>
                    : (new Date(t.expires_at) < new Date() ? <span style={{ color: '#f59e0b' }}>expired</span>
                      : <span style={{ color: '#10b981' }}>active</span>)}
                </td>
                <td style={{ padding: '8px' }}>{t.use_count || 0}{t.last_used_at ? ` · last ${new Date(t.last_used_at).toLocaleDateString()}` : ''}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {!t.revoked_at && <button onClick={() => handleRevoke(t.id)} style={{ ...btnDanger, fontSize: '11px', padding: '3px 8px' }}>Revoke</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <EmbedTokenMintModal
          workspaceId={workspaceId}
          reportId={selectedReportId}
          onClose={() => setCreating(false)}
          onMint={handleMint}
        />
      )}
    </div>
  );
}

function EmbedTokenMintModal({ workspaceId, reportId, onClose, onMint }) {
  const [label, setLabel] = useState('');
  const [hours, setHours] = useState(24);
  const [rlsUser, setRlsUser] = useState('');
  const [theme, setTheme] = useState('auto');
  const [filterOverridesText, setFilterOverridesText] = useState('[]');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    let parsedFilters = [];
    try {
      parsedFilters = filterOverridesText.trim() ? JSON.parse(filterOverridesText) : [];
    } catch (e) {
      setError('filter_overrides must be a JSON array — got: ' + e.message);
      setBusy(false);
      return;
    }
    try {
      await onMint({
        label: label || null,
        expires_in_hours: Number(hours) || 24,
        rls_user_id: rlsUser ? Number(rlsUser) : null,
        theme,
        filter_overrides: parsedFilters,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: '16px' }}>Mint embed token</h3>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>JWT shown once after mint. Revoke any time from the table.</div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginTop: '14px' }}>
          <div>
            <label style={labelStyle}>Label (audit trail)</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Acme Corp portal" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Expires in (hours)</label>
            <input type="number" min="1" max="720" value={hours} onChange={(e) => setHours(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
          <div>
            <label style={labelStyle}>RLS user ID (optional)</label>
            <input type="number" value={rlsUser} onChange={(e) => setRlsUser(e.target.value)} placeholder="42" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Theme</label>
            <select value={theme} onChange={(e) => setTheme(e.target.value)} style={inputStyle}>
              <option value="auto">auto</option>
              <option value="light">light</option>
              <option value="dark">dark</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>Filter overrides (JSON array)</label>
          <textarea
            value={filterOverridesText}
            onChange={(e) => setFilterOverridesText(e.target.value)}
            placeholder='[{"key":"status","op":"eq","value":"open"}]'
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px', minHeight: '80px' }}
          />
        </div>

        {error && <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: '6px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary}>{busy ? 'Minting…' : 'Mint token'}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Templates Gallery (Phase 8)
// ──────────────────────────────────────────────────────────────────────────

export function TemplatesView({ workspaceId, onApplied }) {
  const [reportTemplates, setReportTemplates] = useState([]);
  const [dashTemplates, setDashTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rt, dt] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/reports/templates`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/api/reports/dashboard-templates`, { headers: authHeaders() }),
      ]);
      setReportTemplates(rt.data || []);
      setDashTemplates(dt.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allItems = useMemo(() => {
    return [
      ...reportTemplates.map(t => ({ ...t, _kind: 'report' })),
      ...dashTemplates.map(t => ({ ...t, _kind: 'dashboard' })),
    ];
  }, [reportTemplates, dashTemplates]);

  const categories = useMemo(() => {
    const set = new Set();
    allItems.forEach(it => { if (it.category) set.add(it.category); });
    return ['all', ...Array.from(set).sort()];
  }, [allItems]);

  const visible = useMemo(() => {
    if (filterCat === 'all') return allItems;
    return allItems.filter(it => it.category === filterCat);
  }, [allItems, filterCat]);

  const apply = async (item) => {
    const verb = item._kind === 'dashboard' ? 'dashboard' : 'report';
    if (!window.confirm(`Apply this ${verb} template to the current workspace?`)) return;
    setBusyId(item.id);
    try {
      const url = item._kind === 'dashboard'
        ? `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboard-templates/${item.id}/apply`
        : `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/templates/${item.id}/apply`;
      const r = await axios.post(url, {}, { headers: authHeaders() });
      const created = item._kind === 'dashboard'
        ? `dashboard "${r.data.dashboard.name}" with ${(r.data.reports || []).length} tile(s)`
        : `report "${r.data.name}"`;
      alert(`Created ${created}.`);
      if (onApplied) onApplied(item._kind, r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to apply template');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading templates…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Filter</span>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCat(cat)}
            aria-pressed={filterCat === cat}
            style={{
              ...btnSecondary,
              padding: '4px 10px',
              fontSize: '12px',
              background: filterCat === cat ? 'var(--accent-color)' : 'var(--bg-tertiary)',
              color: filterCat === cat ? '#fff' : 'var(--text-primary)',
              borderColor: filterCat === cat ? 'var(--accent-color)' : 'var(--border-color)',
            }}
          >
            {cat === 'all' ? 'All' : cat}
          </button>
        ))}
      </div>
      {error && <div role="alert" style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: '6px', marginBottom: '12px' }}>{error}</div>}
      {visible.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '32px', color: 'var(--text-tertiary)' }}>No templates in this category.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {visible.map(item => (
            <article key={`${item._kind}-${item.id}`} style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span aria-hidden="true" style={{ fontSize: '20px' }}>{item.icon || (item._kind === 'dashboard' ? '📊' : '📈')}</span>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{item.name}</h3>
                    {item.category && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{item.category}</div>}
                  </div>
                </div>
                <span style={{
                  background: item._kind === 'dashboard' ? 'rgba(34,197,94,0.15)' : 'rgba(99,102,241,0.15)',
                  color: item._kind === 'dashboard' ? '#22c55e' : 'var(--accent-color)',
                  fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px',
                }}>{item._kind === 'dashboard' ? 'Dashboard' : 'Report'}</span>
              </div>
              {item.description && <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 0 0', flex: 1 }}>{item.description}</p>}
              {Array.isArray(item.tags) && item.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {item.tags.slice(0, 4).map(t => (
                    <span key={t} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>{t}</span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', gap: '8px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                  {typeof item.installs === 'number' && item.installs > 0 ? `${item.installs} install${item.installs === 1 ? '' : 's'}` : 'Not yet installed'}
                </div>
                <button
                  onClick={() => apply(item)}
                  disabled={busyId === item.id}
                  aria-label={`Apply ${item.name} template to this workspace`}
                  style={{ ...btnPrimary, fontSize: '12px', padding: '6px 12px' }}
                >
                  {busyId === item.id ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
